import type { IStore } from 'src/stores/Store';

import type { errorCodes } from '../../constants/errorTypes';

import { type TrackJs, getTrackJs } from './../window';

export const checkTrackJSAvailable = (
  trackJs: TrackJs | undefined,
): trackJs is TrackJs =>
  trackJs !== undefined &&
  typeof trackJs.track === 'function' &&
  typeof trackJs.addMetadata === 'function' &&
  typeof trackJs.configure === 'function';

export const configureLogger = (store: IStore) => {
  // TODO_TYPES: trackJs assertions on the window
  const trackJs = getTrackJs();

  if (checkTrackJSAvailable(trackJs)) {
    const { userStore } = store;
    if (userStore.user.userId) {
      trackJs.configure({
        userId: `${userStore.user.userId}`,
      });
    }
    trackJs.addMetadata('advertiserId', `${store.advertiserId}`);
    trackJs.addMetadata('positionId', `${store.positionId}`);
  }
};

type ErrorCodes = (typeof errorCodes)[keyof typeof errorCodes];

interface Information {
  errorCode: ErrorCodes;
  description?: string;
}

const formatTrackJsMessage = ({ errorCode, description }: Information) =>
  `${errorCode}${description ? ` | ${description}` : ''}`;

/**
 * @function logError
 * @description Sends error to tracking service (TrackJS)
 * @param {Information} information: errorCode and description of the error to be logged. Be sure not to include dynamic data here.
 * @param {Record<string, any>} debugInfo: optional debug information to describe what occurred.
 *
 * Basic Usage:
 * logError({ errorCode: 'SOME_ERROR_CODE', description: some description }, { error, anyDebugInfo});
 */
export const logError = (
  information: Information,
  debugInfo?: Record<string, any>,
) => {
  const trackJs = getTrackJs();
  const message = formatTrackJsMessage(information);

  if (checkTrackJSAvailable(trackJs)) {
    if (debugInfo) {
      trackJs.console.info(message, debugInfo);
    }

    trackJs.track(message);
  } else {
    // eslint-disable-next-line no-console
    console.error({ message, debugInfo });
  }
};
